import type { Field } from '@lighthouse/core'
import { VariableType } from '@lighthouse/core'
import type { FlowNode } from '@lighthouse/shared'
import {
    type AliyunMessageActionConfig,
    getAllCanBeUpstreamNodes,
    getUpstreamNodesOptions,
    isRichTextValue,
    useAtomData
} from '@lighthouse/shared'
import { useNodesData } from '@xyflow/react'
import { useMemo } from 'react'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetUserRecordOptions } from '@/hooks/usePage'
import { useGetAliyunMessageInfo } from '@/shared/reusable'

import * as CM from '../styles'
import { textRichVariableName } from '../util'
import * as SC from './styles'

type ALiYunMessageCardProps = {
    config: AliyunMessageActionConfig
}

const ALiYunMessageCard = ({ config }: ALiYunMessageCardProps) => {
    const { mobileList, signatureId, templateId } = config
    const otherPlatformList = useAtomData(otherPlatformListAtom)

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const aliyunSmsAccount = useMemo(() => otherPlatformList?.find(item => item.type === 'ALIYUN_SMS'), [otherPlatformList])

    const { data } = useGetAliyunMessageInfo(aliyunSmsAccount?.id)
    const userOption = useGetUserRecordOptions()

    const nodeId = useMemo(() => {
        if (mobileList?.type === VariableType.UPSTREAM) {
            const nodeId = mobileList?.upstreamVariable?.nodeId
            if (nodeId) {
                return nodeId
            }
        }
        return ''
    }, [mobileList])

    const nodes = useNodesData([nodeId]) as FlowNode[]

    const textUpstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: getAllCanBeUpstreamNodes(nodes || []),
                allUpstreamNodes: nodes || []
            }),
        [workflowList, dataSourceList, nodes]
    )

    const phoneText = useMemo(() => {
        if (mobileList?.type === VariableType.VALUE) {
            const result = mobileList?.valueVariable?.value || ''
            if (typeof result === 'string') {
                return result
            }
            if (isRichTextValue(result)) {
                return ''
            }
            if (typeof result === 'object') {
                return result?.join(',')
            }
            return typeof result === 'string' ? result : JSON.stringify(result)
        }
        return textRichVariableName({
            userOption,
            upstreamOptions: textUpstreamOptions,
            // pageOptions: isNotAutomation ? [PAGE_LINK_OPTION, ...pageOptions] : undefined,
            data: mobileList
        })
    }, [mobileList, userOption, textUpstreamOptions])

    const templateTitle = useMemo(() => {
        const title = data?.templates.find(item => item.id === templateId)
        return title?.name
    }, [data?.templates, templateId])

    if (!aliyunSmsAccount?.configurationComplete) {
        return null
    }

    return (
        <CM.CardContainer>
            {phoneText && phoneText.length > 0 && (
                <CM.CardItemContainer>
                    <SC.TypeName>手机号</SC.TypeName>
                    <CM.FieldItemContent>
                        <CM.FieldItemTitle
                            whiteSpace="pre-wrap"
                            color={!mobileList || mobileList.type === VariableType.VALUE ? undefined : 'var(--color-purple-500)'}
                        >
                            {phoneText}
                        </CM.FieldItemTitle>
                    </CM.FieldItemContent>
                </CM.CardItemContainer>
            )}

            {signatureId && (
                <CM.CardItemContainer>
                    <SC.TypeName>短信签名</SC.TypeName>
                    <CM.FieldItemContent>
                        <CM.FieldItemTitle whiteSpace="pre-wrap">{signatureId}</CM.FieldItemTitle>
                    </CM.FieldItemContent>
                </CM.CardItemContainer>
            )}

            {templateTitle && (
                <CM.CardItemContainer>
                    <SC.TypeName>短信模板</SC.TypeName>
                    <CM.FieldItemContent>
                        <CM.FieldItemTitle whiteSpace="pre-wrap">{templateTitle}</CM.FieldItemTitle>
                    </CM.FieldItemContent>
                </CM.CardItemContainer>
            )}
        </CM.CardContainer>
    )
}

export default ALiYunMessageCard
